/**
 * Builds the Pitch's player navigator (SE-8605): the same expanded player URL
 * and two-crumb trail the Table view pushes, with the origin crumb supplied by
 * the Pitch that hosts the names (a watchlist or a squad scenario).
 * @module features/scouting/hooks/usePitchPlayerNavigation
 */
import { type Href, useRouter } from "expo-router";
import { useCallback } from "react";

import type { OpenPitchPlayer } from "@/features/scouting/contexts/PitchPlayerNavigationContext";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import {
  type NavigationSource,
  useNavigationContext,
} from "@/providers/NavigationProvider";

/** Where the player page's back crumb leads, per hosting Pitch. */
export interface PitchPlayerOrigin {
  /** The origin crumb's label ("Back to Squad", a list name). */
  readonly label: string;
  /** Where the origin crumb returns to. */
  readonly path: string;
  /** Navigation source the player page reads, when the origin sets one. */
  readonly source?: NavigationSource;
}

/**
 * Navigator that opens `/players/{id}` behind an origin crumb.
 * @param origin - The hosting Pitch's back crumb and navigation source
 * @returns A stable navigator taking the player id and display name
 */
export const usePitchPlayerNavigation = (
  origin: PitchPlayerOrigin
): OpenPitchPlayer => {
  const { label, path, source } = origin;
  const router = useRouter();
  const getExpandedPlayerUrl = useGetExpandedPlayerUrl();
  const { clearBreadcrumbs, pushBreadcrumb, setNavigationSource } =
    useNavigationContext();
  return useCallback(
    (playerId: string, displayName: string) => {
      const playerUrl = getExpandedPlayerUrl(`/players/${playerId}`);
      clearBreadcrumbs();
      pushBreadcrumb({ label, path });
      pushBreadcrumb({ label: displayName || "Player", path: playerUrl });
      if (source !== undefined) setNavigationSource(source);
      router.push(playerUrl as Href);
    },
    [
      clearBreadcrumbs,
      getExpandedPlayerUrl,
      label,
      path,
      pushBreadcrumb,
      router,
      setNavigationSource,
      source,
    ]
  );
};
